import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useRolesAndPermissions } from '../../hooks/permissionHooks/useRolesAndPermissions';
import { usePermissionCheck } from '../../hooks/permissionHooks/usePermissionCheck';
import { Shield, AlertTriangle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { hasPermission, getRoutePermission } from '../../config/permissions';

const PermissionGuard = ({
  module,
  action,
  requireAll = false,
  children,
  fallback = null,
  customCheck = null,
  showFallback = false
}) => {
  const user = useSelector((state) => state?.user?.user);
  const location = useLocation();
  const { canPerform, canPerformAny, canPerformAll, isMainVendor } = usePermissionCheck();

  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (isMainVendor) {
    return children;
  }

  if (customCheck && typeof customCheck === 'function') {
    const hasPermission = customCheck();
    if (hasPermission) {
      return children;
    }
    return showFallback ? fallback : null;
  }

  if (!module || !action) {
    console.warn('PermissionGuard: module and action props are required');
    return showFallback ? fallback : null;
  }

  let hasPermission = false;

  if (Array.isArray(action)) {
    if (requireAll) {
      hasPermission = canPerformAll(module, action);
    } else {
      hasPermission = canPerformAny(module, action);
    }
  } else {
    hasPermission = canPerform(module, action);
  }

  if (hasPermission) {
    return children;
  }

  return showFallback ? fallback : null;
};

export const ProtectedRoute = ({ 
  children, 
  module = null, 
  action = null, 
  requiresVerification = true 
}) => {
  const user = useSelector((state) => state?.user?.user);
  const location = useLocation();

  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (requiresVerification && !user.email_verified_at && !user.phone_number_verified_at) {
    return <Navigate to="/verify-otp" state={{ from: location }} replace />;
  }

  if (!module || !action) {
    return children;
  }

  return (
    <PermissionGuard module={module} action={action}>
      {children}
    </PermissionGuard>
  );
};

export const PermissionWrapper = ({ 
  children, 
  module, 
  action, 
  fallback = null,
  inverse = false 
}) => {
  const user = useSelector((state) => state?.user?.user);
  const { canUserPerformAction } = useRolesAndPermissions();

  if (user?.is_main_vendor || user?.role === 'admin') {
    return inverse ? fallback : children;
  }

  const hasPermission = user ? canUserPerformAction(user.id, module, action) : false;
  
  if (inverse) {
    return hasPermission ? fallback : children;
  }
  
  return hasPermission ? children : fallback;
};

export const RoleWrapper = ({ 
  children, 
  roles = [], 
  fallback = null,
  inverse = false 
}) => {
  const user = useSelector((state) => state?.user?.user);

  if (!user) return fallback;

  if (user.is_main_vendor || user.role === 'admin') {
    return inverse ? fallback : children;
  }

  const hasRole = roles.includes(user.role?.toLowerCase());
  
  if (inverse) {
    return hasRole ? fallback : children;
  }
  return hasRole ? children : fallback;
};

// Pre-built permission guards for common use cases
export const RolesPermissionGuard = ({ children, action, fallback, showFallback = false }) => (
  <PermissionGuard
    module="Roles & Permission"
    action={action}
    fallback={fallback}
    showFallback={showFallback}
  >
    {children}
  </PermissionGuard>
);

export const DealsPermissionGuard = ({ children, action, fallback, showFallback = false }) => (
  <PermissionGuard
    module="Deals Management"
    action={action}
    fallback={fallback}
    showFallback={showFallback}
  >
    {children}
  </PermissionGuard>
);

export const PartnersPermissionGuard = ({ children, action, fallback, showFallback = false }) => (
  <PermissionGuard
    module="Partner Management"
    action={action}
    fallback={fallback}
    showFallback={showFallback}
  >
    {children}
  </PermissionGuard>
);

export const SubscriptionPermissionGuard = ({ children, action, fallback, showFallback = false }) => (
  <PermissionGuard
    module="Subscription"
    action={action}
    fallback={fallback}
    showFallback={showFallback}
  >
    {children}
  </PermissionGuard>
);

export const AnalyticsPermissionGuard = ({ children, action, fallback, showFallback = false }) => (
  <PermissionGuard
    module="Deals Analytics"
    action={action}
    fallback={fallback}
    showFallback={showFallback}
  >
    {children}
  </PermissionGuard>
);

// Hook for permission checking in components
export const useRoleBasedAccess = () => {
  const permissionCheck = usePermissionCheck();
  
  return {
    ...permissionCheck,
    // Convenience methods for specific modules
    roles: {
      canView: () => permissionCheck.canPerform("Roles & Permission", "View"),
      canCreate: () => permissionCheck.canPerform("Roles & Permission", "Create"),
      canEdit: () => permissionCheck.canPerform("Roles & Permission", "Edit"),
      canDelete: () => permissionCheck.canPerform("Roles & Permission", "Delete"),
      canManage: () => permissionCheck.canPerformAny("Roles & Permission", ["View", "Create", "Edit"])
    },
    deals: {
      canView: () => permissionCheck.canPerform("Deals Management", "View Deals"),
      canCreate: () => permissionCheck.canPerform("Deals Management", "Create Offer"),
      canEdit: () => permissionCheck.canPerform("Deals Management", "Edit Offer"),
      canDelete: () => permissionCheck.canPerform("Deals Management", "Delete Offer"),
      canBlast: () => permissionCheck.canPerform("Deals Management", "Blast Deal")
    },
    partners: {
      canView: () => permissionCheck.canPerform("Partner Management", "View Partners"),
      canCreate: () => permissionCheck.canPerform("Partner Management", "Create Partner"),
      canEdit: () => permissionCheck.canPerform("Partner Management", "Edit Partner"),
      canDelete: () => permissionCheck.canPerform("Partner Management", "Delete Partner")
    }
  };
};

const PermissionGuardRoute = ({ children, fallbackComponent = null }) => {
  const { user } = useSelector(state => state.user);
  const location = useLocation();
  const navigate = useNavigate();

  // First check if user is authenticated
  if (!user) {
    // User is not authenticated, let ProtectedRoute handle this
    return children;
  }

  // Check if user has permission for current route
  const checkRoutePermission = () => {
    const currentPath = location.pathname;
    
    // Get permission requirement for this route
    const routePermission = getRoutePermission(currentPath);
    
    // If no permission requirement, allow access
    if (!routePermission) return true;
    
    const { module, action } = routePermission;
    
    // Main vendor check - some routes are restricted to main vendors only
    const restrictedToMainVendor = [
      // '/roles-and-permissions',
      // '/roles',
      // '/roles/manage',
      // '/roles/create',
      // '/roles/team-member/create'
    ];
    
    if (restrictedToMainVendor.some(path => currentPath.startsWith(path))) {
      if (!user?.is_main_vendor) {
        return false;
      }
    }
    
    // Check user permissions
    const userPermissions = user?.permissions || {};
    const isMainVendor = user?.is_main_vendor || false;
    
    if (isMainVendor) return true;

    return hasPermission(userPermissions, isMainVendor, module, action);
  };

  const hasAccess = checkRoutePermission();

  if (hasAccess) {
    return children;
  }

  if (fallbackComponent) {
    return fallbackComponent;
  }
  return (
    <div className="flex justify-center items-center p-4 min-h-screen bg-gray-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="p-8 my-auto w-full max-w-md text-center bg-white rounded-2xl shadow-xl max-h-[450px]"
      >
        {/* Icon */}
        <motion.div
          initial={{ rotate: -10 }}
          animate={{ rotate: 0 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="flex justify-center items-center mx-auto mb-6 w-16 h-16 bg-red-100 rounded-full"
        >
          <Shield className="w-8 h-8 text-red-600" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-3 text-xl font-bold text-gray-900"
        >
          Access Restricted
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-6 text-sm leading-relaxed text-gray-600"
        >
          You don't have permission to access this page. Please contact your administrator to request access or check if you're using the correct account.
        </motion.p>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col gap-3 sm:flex-row"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/dashboard')}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#FF6C6C] text-white rounded-lg font-medium transition-colors hover:bg-[#FF6C6C]/90"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Dashboard
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(-1)}
            className="flex-1 px-4 py-3 font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
          >
            Go Back
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PermissionGuardRoute; 